// Mock data for the /mockup/student UI prototype. No backend calls — swap for
// CourseApi / LessonApi / UserDialogPromptApi.getCurrentUserSummary() when wiring for real.

import { BLUE, GREEN, NAVY } from './studentStyles';

export interface StudentLesson {
    id: number;
    name: string;
    totalPrompts: number;
    completedPrompts: number;
}

export type CourseIconKey = 'book' | 'chat' | 'voice';

export interface StudentCourse {
    id: number;
    name: string;
    subtitle: string;
    accentColor: string;
    icon: CourseIconKey;
    lessons: StudentLesson[];
}

export const mockCourses: StudentCourse[] = [
    {
        id: 1,
        name: "Chinese 1 – Foundations",
        subtitle: "First words and phrases",
        accentColor: BLUE,
        icon: 'book',
        lessons: [
            { id: 101, name: "Lesson 1: Greetings & Introductions", totalPrompts: 18, completedPrompts: 18 },
            { id: 102, name: "Lesson 2: Numbers & Dates", totalPrompts: 22, completedPrompts: 22 },
            { id: 103, name: "Lesson 3: Family", totalPrompts: 20, completedPrompts: 20 },
        ],
    },
    {
        id: 2,
        name: "Chinese 2 – Daily Life",
        subtitle: "Conversations you can use today",
        accentColor: GREEN,
        icon: 'chat',
        lessons: [
            // 33 − 19 authored: resume lands on the restaurant arc (also makes 201 the current assignment)
            { id: 201, name: "Lesson 4: Ordering at a Restaurant", totalPrompts: 33, completedPrompts: 14 },
            // 33 − 17 authored: resume lands on the weekend arc
            { id: 202, name: "Lesson 5: Weekend Plans", totalPrompts: 33, completedPrompts: 16 },
            { id: 203, name: "Lesson 6: Asking for Directions", totalPrompts: 21, completedPrompts: 0 },
            { id: 204, name: "Lesson 7: Shopping & Prices", totalPrompts: 23, completedPrompts: 0 },
        ],
    },
    {
        id: 3,
        name: "Pronunciation Lab",
        subtitle: "Tone practice with instant feedback",
        accentColor: NAVY,
        icon: 'voice',
        lessons: [
            { id: 301, name: "Tones 1 & 2 Drills", totalPrompts: 15, completedPrompts: 15 },
            { id: 302, name: "Tones 3 & 4 Drills", totalPrompts: 17, completedPrompts: 6 },
            { id: 303, name: "Tone Pairs Challenge", totalPrompts: 20, completedPrompts: 0 },
        ],
    },
];

export function lessonPercent(lesson: StudentLesson): number {
    if (lesson.totalPrompts === 0) {
        return 0;
    }
    return Math.round((lesson.completedPrompts / lesson.totalPrompts) * 100);
}

export function completedLessonCount(course: StudentCourse): number {
    return course.lessons.filter(lesson => lessonPercent(lesson) === 100).length;
}

export function coursePercent(course: StudentCourse): number {
    const total = course.lessons.reduce((sum, lesson) => sum + lesson.totalPrompts, 0);
    const done = course.lessons.reduce((sum, lesson) => sum + lesson.completedPrompts, 0);
    if (total === 0) {
        return 0;
    }
    return Math.round((done / total) * 100);
}

export interface CurrentAssignment {
    course: StudentCourse;
    lesson: StudentLesson;
    percent: number;
}

export function getCurrentAssignment(): CurrentAssignment | null {
    for (const course of mockCourses) {
        for (const lesson of course.lessons) {
            const percent = lessonPercent(lesson);
            if (percent > 0 && percent < 100) {
                return { course, lesson, percent };
            }
        }
    }
    return null;
}

// Session-only progress: mutating the module-level mock keeps the Home/CourseDetail
// rings in sync when navigating back. Replaying a finished exercise never regresses.
export function markExerciseComplete(lesson: StudentLesson, exerciseIndex: number): void {
    lesson.completedPrompts = Math.min(lesson.totalPrompts, Math.max(lesson.completedPrompts, exerciseIndex + 1));
}
